import { msg } from "@lingui/core/macro"
import { useLingui } from "@lingui/react"
import { Plural, Trans } from "@lingui/react/macro"
import { IconAlertTriangle, IconCopy, IconPlayerPauseFilled, IconUsers } from "@tabler/icons-react"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import type { IStreamingSegment } from "../../hooks/useStreamingTranscript"
import { formatClock } from "../../lib/format-date"
import { identityTextColor, normalizeSpeakerKey, resolveSpeakerColor, SPEAKER_COLORS } from "../../lib/speaker-colors"
import { type ISpeakerVM, speakerDisplayLabel, speakerVMFromName } from "../../lib/speaker-vm"
import { SpeakerChip } from "../SpeakerChip"
import { TypewriterText } from "../TypewriterText"
import classes from "./TranscriptCard.module.css"
import { joinSpeakerRun, needsSpaceBetween, transcriptContentSignature } from "./transcript-text"

interface IProps {
  segments: IStreamingSegment[]
  /** Interim partial transcript — appended to the latest line as decorative italic. */
  interimText: string
  interimSpeaker: string
  paused: boolean
  /** Map from speaker label/name → hex color. Falls back to the canonical SPEAKER_COLORS palette. */
  speakerColors?: Map<string, string>
  /** True when the audio meter is reading clipping levels. Surfaces a warning meta. */
  micClipping: boolean
  /**
   * Read-only mode — drops the cursor, paused-overlay banner, and any
   * interim-text styling. Used during finalize so the transcript stays visible
   * while the analysis pipeline finishes.
   */
  readOnly?: boolean
  /**
   * Embedded mode — renders without its own card chrome (border/background/
   * tall min-height) so it can live inside another surface, e.g. the capture
   * monitor's pull-tab reveal.
   */
  embedded?: boolean
  /**
   * Fill the space under the recording bar. Lite uses this so the transcript
   * is the page, not a 340px panel inside the capture card.
   */
  fill?: boolean
}

/** Group of consecutive segments from the same speaker — the unit the
 * transcript actually renders. Multiple segments are merged into one slot
 * so a speaker who keeps talking doesn't get a fresh chip per sentence. */
interface ISpeakerGroup {
  speaker: string
  start: number
  texts: string[]
}

function groupBySpeaker(segments: IStreamingSegment[]): ISpeakerGroup[] {
  const groups: ISpeakerGroup[] = []
  for (const seg of segments) {
    const last = groups[groups.length - 1]
    if (last && last.speaker === seg.speaker) {
      last.texts.push(seg.text)
    } else {
      groups.push({ speaker: seg.speaker, start: seg.start, texts: [seg.text] })
    }
  }
  return groups
}

function speakerColorFor(name: string, idx: number, override: Map<string, string> | undefined): string {
  // The shared normalised map lookup lives in `resolveSpeakerColor`; reuse it when
  // the label is mapped. For an UNMAPPED live label we deliberately keep this
  // surface's first-appearance order-index fallback (SPEAKER_COLORS[idx]) instead of
  // the lib's name-hash, so a speaker's hue stays put as "Speaker_1" resolves to a
  // real name mid-recording rather than jumping to a hash-derived colour.
  const mapped = override?.has(normalizeSpeakerKey(name)) || override?.has(name)
  return mapped ? resolveSpeakerColor(name, override) : SPEAKER_COLORS[idx % SPEAKER_COLORS.length]
}

/**
 * Minimal avatar view-model for a live transcript speaker: the shared
 * `speakerVMFromName` derivation (display name / initials / far-end), with the
 * already-resolved order-index or mapped hue forced onto `resolvedColor`.
 */
function transcriptSpeakerVM(name: string, color: string): ISpeakerVM {
  return { ...speakerVMFromName(name), resolvedColor: color }
}

export function TranscriptCard({
  segments,
  interimText,
  interimSpeaker,
  paused,
  speakerColors,
  micClipping,
  readOnly = false,
  embedded = false,
  fill = false,
}: IProps): React.JSX.Element {
  const { i18n } = useLingui()
  const bodyRef = useRef<HTMLDivElement>(null)
  const ignoreScrollRef = useRef(false)
  const [following, setFollowing] = useState(true)

  // Stable speaker order — first appearance wins so palette index stays stable.
  const speakerOrder = useMemo(() => {
    const order: string[] = []
    for (const seg of segments) {
      if (!order.includes(seg.speaker)) order.push(seg.speaker)
    }
    if (interimSpeaker && !order.includes(interimSpeaker)) order.push(interimSpeaker)
    return order
  }, [segments, interimSpeaker])

  // Merge consecutive segments by speaker so a speaker who keeps talking
  // gets one slot instead of a fresh chip per sentence.
  const groups = useMemo(() => groupBySpeaker(segments), [segments])

  // When the latest group's speaker matches the interim speaker, we fold the
  // interim text into that group instead of rendering a separate slot.
  const interimMergesIntoLast =
    !!interimText && !!interimSpeaker && groups.length > 0 && groups[groups.length - 1].speaker === interimSpeaker

  // Any text change, including a final that replaces an interim of the same
  // length inside an existing speaker run. A length sum stays put in that case
  // and the view stops following.
  const contentSignature = useMemo(
    () => transcriptContentSignature(segments, interimText, interimSpeaker),
    [segments, interimText, interimSpeaker],
  )

  const scrollToLatest = useCallback((): void => {
    const el = bodyRef.current
    if (!el) return
    ignoreScrollRef.current = true
    el.scrollTop = el.scrollHeight
    requestAnimationFrame(() => {
      const node = bodyRef.current
      if (node) node.scrollTop = node.scrollHeight
      ignoreScrollRef.current = false
    })
  }, [])

  // Follow the newest line until the reader scrolls up.
  // biome-ignore lint/correctness/useExhaustiveDependencies: contentSignature is the scroll trigger when a final lands
  useEffect(() => {
    if (!following) return
    scrollToLatest()
  }, [contentSignature, following, scrollToLatest])

  const handleScroll = useCallback((): void => {
    if (ignoreScrollRef.current) return
    const el = bodyRef.current
    if (!el) return
    const distance = el.scrollHeight - el.scrollTop - el.clientHeight
    setFollowing(distance <= 32)
  }, [])

  const handleJump = useCallback((): void => {
    setFollowing(true)
    scrollToLatest()
  }, [scrollToLatest])

  const handleCopy = async (): Promise<void> => {
    const text = segments
      .map((seg) => {
        const ts = formatClock(seg.start)
        const vm = transcriptSpeakerVM(seg.speaker, speakerColorFor(seg.speaker, 0, speakerColors))
        return `[${ts}] ${speakerDisplayLabel(vm, i18n)}: ${seg.text}`
      })
      .join("\n")
    try {
      await navigator.clipboard.writeText(text)
    } catch {
      // Clipboard not granted — silent failure (button is decorative aid).
    }
  }

  const empty = groups.length === 0 && !interimText
  const lastGroupIdx = groups.length - 1

  // Standalone interim slot is only needed when the interim speaker doesn't
  // match the last group's speaker (e.g. someone new starts talking).
  const showStandaloneInterim = !!interimText && !!interimSpeaker && !interimMergesIntoLast
  const interimColor = speakerColorFor(interimSpeaker, speakerOrder.indexOf(interimSpeaker), speakerColors)

  return (
    <div
      className={classes.card}
      data-embedded={embedded ? "true" : "false"}
      data-fill={fill ? "true" : "false"}
      data-testid="recording-transcript"
    >
      <div className={classes.head}>
        <div className={classes.title}>
          <span className={classes.overline}>
            <Trans>Live transcript</Trans>
          </span>
          <span className={classes.meta}>
            <IconUsers size={12} />
            <Plural value={speakerOrder.length} one="# voice so far" other="# voices so far" />
          </span>
        </div>
        <div className={classes.actions}>
          {micClipping && (
            <span className={`${classes.meta} ${classes.metaWarning}`}>
              <IconAlertTriangle size={13} />
              <Trans>Mic level high</Trans>
            </span>
          )}
          <button
            type="button"
            className={classes.action}
            title={i18n._(msg`Copy transcript`)}
            aria-label={i18n._(msg`Copy transcript`)}
            onClick={() => handleCopy().catch(() => undefined)}
          >
            <IconCopy size={14} />
          </button>
        </div>
      </div>

      {paused && !readOnly && (
        <div className={classes.pausedOverlay}>
          <IconPlayerPauseFilled size={14} />
          <Trans>Recording is paused. Resume to continue capturing.</Trans>
        </div>
      )}

      <div className={classes.body} ref={bodyRef} onScroll={handleScroll} data-empty={empty ? "true" : "false"}>
        {empty && <Trans>Waiting for first words…</Trans>}
        {groups.map((group, idx) => {
          const isLast = idx === lastGroupIdx
          const isLatest = isLast && !paused && !readOnly && !showStandaloneInterim
          const speakerIdx = speakerOrder.indexOf(group.speaker)
          const color = speakerColorFor(group.speaker, speakerIdx, speakerColors)
          // When the interim merges into this last group's speaker, render its
          // text inline (italic) at the end of the paragraph.
          const trailingInterim = isLast && interimMergesIntoLast ? interimText : ""
          const lineText = joinSpeakerRun(group.texts)
          const interimSpaced = trailingInterim.length > 0 && needsSpaceBetween(lineText, trailingInterim)
          return (
            <div
              // Key on the (stable) start time only — NOT the speaker — so the
              // line element persists when a label resolves (Speaker_1 → Gerard).
              // That lets the speaker name typewriter-rename in place instead of
              // the whole line remounting + re-popping.
              key={group.start}
              className={`${classes.line} ${isLatest ? classes.lineLatest : ""}`}
              data-speaker={group.speaker}
            >
              <span className={classes.timestamp}>{formatClock(group.start)}</span>
              <div className={classes.lineBody}>
                <div className={classes.speakerRow}>
                  <SpeakerChip variant="avatar" size="sm" speaker={transcriptSpeakerVM(group.speaker, color)} />
                  <span className={classes.speakerName} style={{ color: identityTextColor(color) }}>
                    <TypewriterText value={speakerDisplayLabel(transcriptSpeakerVM(group.speaker, color), i18n)} />
                  </span>
                </div>
                <p className={classes.text}>
                  {lineText}
                  {trailingInterim && (
                    <>
                      {interimSpaced ? " " : null}
                      <span className={classes.interim}>{trailingInterim.trim()}</span>
                    </>
                  )}
                  {isLatest && (
                    <span className={`${classes.cursor} ${paused ? classes.cursorPaused : ""}`} aria-hidden="true" />
                  )}
                </p>
              </div>
            </div>
          )
        })}
        {showStandaloneInterim && (
          <div className={`${classes.line} ${classes.lineLatest}`}>
            <span className={classes.timestamp}>—</span>
            <div className={classes.lineBody}>
              <div className={classes.speakerRow}>
                <SpeakerChip variant="avatar" size="sm" speaker={transcriptSpeakerVM(interimSpeaker, interimColor)} />
                <span className={classes.speakerName} style={{ color: identityTextColor(interimColor) }}>
                  {speakerDisplayLabel(transcriptSpeakerVM(interimSpeaker, interimColor), i18n)}
                </span>
              </div>
              <p className={`${classes.text} ${classes.interim}`}>
                {interimText}
                {!readOnly && (
                  <span className={`${classes.cursor} ${paused ? classes.cursorPaused : ""}`} aria-hidden="true" />
                )}
              </p>
            </div>
          </div>
        )}
      </div>
      {!following && !empty && (
        <button type="button" className={classes.jump} onClick={handleJump} data-testid="jump-to-latest">
          <Trans>Jump to latest</Trans>
        </button>
      )}
    </div>
  )
}
